import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { CommonMenuDetails } from '@api/editorialLayout/menuAdmin';
import MenuItem from '@models/MenuItem';
import { CrudAttrs } from '@models/WithCrud';

// Metadata fields that don't affect the UI when changed
const METADATA_FIELDS = Object.keys({} as CrudAttrs) as readonly (keyof CrudAttrs)[];

// Generic initial state factory
const createEmptyMenu = <T extends CommonMenuDetails>(isRootMenu?: boolean): T => {
  const baseMenu = {
    createdBy: '',
    createdDateTime: '',
    description: '',
    id: '',
    menuItems: [],
    name: '',
    slug: '',
    updatedBy: '',
    updatedDateTime: '',
    ...(isRootMenu ? { deviceTypeId: '', countries: [] } : {}),
  };

  return baseMenu as unknown as T;
};

export const createEmptyMenuItem = (): MenuItem => ({
  deviceTypeIds: [],
  endTime: null,
  icon: '',
  image: '',
  label: { no: '', da: '', en: '' },
  locationIds: [],
  partnerIds: [],
  slug: '',
  sourceId: '',
  startTime: null,
  type: 'page',
});

// Generic state interface
interface MenuState<T extends CommonMenuDetails = CommonMenuDetails> {
  menuDraft: T;
  originalMenu: T | null;
  isLoading: boolean;
  hasUnsavedChanges: boolean;
  isInitialized: boolean;
  isRootMenu: boolean;
}

// Initial state
const initialState: MenuState = {
  menuDraft: createEmptyMenu(),
  originalMenu: null,
  isLoading: false,
  hasUnsavedChanges: false,
  isInitialized: false,
  isRootMenu: false,
};

// Redux Toolkit slice
const menuSlice = createSlice({
  name: 'menu',
  initialState,
  reducers: {
    // Initialize menu from server data
    initializeMenu: (state, action: PayloadAction<CommonMenuDetails>) => {
      state.menuDraft = action.payload;
      state.originalMenu = action.payload;
      state.hasUnsavedChanges = false;
      state.isInitialized = true;
      // Detect if it's a root menu based on presence of deviceTypeId
      state.isRootMenu = 'deviceTypeId' in action.payload;
    },

    // Create new menu
    createNewMenu: (state, action: PayloadAction<{ isRootMenu?: boolean }>) => {
      const isRootMenu = action.payload.isRootMenu ?? false;
      const newMenu = createEmptyMenu(isRootMenu);
      state.menuDraft = newMenu;
      state.originalMenu = null;
      state.hasUnsavedChanges = true;
      state.isInitialized = true;
      state.isRootMenu = isRootMenu;
    },

    // Generic field update
    updateField: (
      state,
      action: PayloadAction<{ field: keyof CommonMenuDetails; value: unknown }>
    ) => {
      const { field, value } = action.payload;
      (state.menuDraft as Record<keyof CommonMenuDetails, unknown>)[field] = value;
      state.hasUnsavedChanges = true;
    },

    // Smart name and slug update
    updateNameAndSlug: (state, action: PayloadAction<{ name: string; slug?: string }>) => {
      const { name, slug } = action.payload;
      if (name != null) state.menuDraft.name = name;
      if (slug != null) state.menuDraft.slug = slug;
      state.hasUnsavedChanges = true;
    },

    // Menu items management
    setMenuItems: (state, action: PayloadAction<MenuItem[]>) => {
      state.menuDraft.menuItems = action.payload;
      state.hasUnsavedChanges = true;
    },

    addMenuItem: (state, action: PayloadAction<{ index: number; item: MenuItem }>) => {
      const { index, item } = action.payload;
      state.menuDraft.menuItems.splice(index, 0, item);
      state.hasUnsavedChanges = true;
    },

    updateMenuItem: (state, action: PayloadAction<{ index: number; item: MenuItem }>) => {
      const { index, item } = action.payload;
      state.menuDraft.menuItems[index] = item;
      state.hasUnsavedChanges = true;
    },

    removeMenuItem: (state, action: PayloadAction<number>) => {
      state.menuDraft.menuItems.splice(action.payload, 1);
      state.hasUnsavedChanges = true;
    },

    // Reset to original state
    resetToOriginal: state => {
      if (state.originalMenu) {
        state.menuDraft = { ...state.originalMenu };
        state.hasUnsavedChanges = false;
      }
    },

    // Loading states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    // Mark as saved (after successful save)
    markAsSaved: (state, action: PayloadAction<CommonMenuDetails>) => {
      const { payload: newMenu } = action;
      const { menuDraft } = state;

      // Check if only metadata fields changed
      const nonMetadataFields = Object.keys(newMenu).filter(
        key => !METADATA_FIELDS.includes(key as keyof CrudAttrs)
      ) as (keyof CommonMenuDetails)[];

      const hasContentChanges = nonMetadataFields.some(
        key => JSON.stringify(menuDraft[key]) !== JSON.stringify(newMenu[key])
      );

      // Only update current menu if there are content changes
      if (hasContentChanges) state.menuDraft = newMenu;
      else {
        // Update only metadata fields to avoid re-render
        METADATA_FIELDS.forEach(field => {
          if (field in newMenu) {
            (state.menuDraft as Record<keyof CrudAttrs, unknown>)[field] = newMenu[field];
          }
        });
      }

      state.originalMenu = newMenu;
      state.hasUnsavedChanges = false;
    },

    // Clear menu state (for cleanup on unmount)
    clearMenu: state => {
      state.menuDraft = createEmptyMenu();
      state.originalMenu = null;
      state.hasUnsavedChanges = false;
      state.isLoading = false;
      state.isInitialized = false;
      state.isRootMenu = false;
    },
  },
});

// Export actions
export const {
  initializeMenu,
  createNewMenu,
  updateField,
  updateNameAndSlug,
  setMenuItems,
  addMenuItem,
  updateMenuItem,
  removeMenuItem,
  resetToOriginal,
  setLoading,
  markAsSaved,
  clearMenu,
} = menuSlice.actions;

// Selectors
export const selectMenuDraft = (state: { menu: MenuState }) => state.menu.menuDraft;
export const selectOriginalMenu = (state: { menu: MenuState }) => state.menu.originalMenu;
export const selectMenuItems = (state: { menu: MenuState }) => state.menu.menuDraft.menuItems;
export const selectHasUnsavedChanges = (state: { menu: MenuState }) => state.menu.hasUnsavedChanges;
export const selectIsLoading = (state: { menu: MenuState }) => state.menu.isLoading;
export const selectIsInitialized = (state: { menu: MenuState }) => state.menu.isInitialized;
export const selectIsNewMenu = (state: { menu: MenuState }) => !state.menu.originalMenu;
export const selectIsRootMenu = (state: { menu: MenuState }) => state.menu.isRootMenu;

export const { actions: menuActions } = menuSlice;

export default menuSlice.reducer;
